import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useToastStore } from '@pkg/components/Toast/useToastStore';
import { useQueryInvalidation } from '@pkg/hooks/reactQuery';
import { useAppDispatch } from '@pkg/hooks/redux';
import { useUpdatePrimaryYat as useUpdatePrimaryYatMutation } from '@pkg/hooks/user/useUpdateUser';

import { type EmojiId } from '@app/client';
import { USER_YATS_QUERY_CACHE } from '@app/pages/ProfilePage/sections/YatsSection/hooks/useGetUserYats';
import { setPrimaryYat } from '@app/store/actions/user';
import { getYatPageDetails } from '@app/store/actions/yat-page';
import { USER_PROFILE_YATS_CACHE } from '@app/store/zustand/profile-page/useProfileYats';
//import { useToggleYatsVisibilityMutation } from '@app/store/zustand/profile-page/useToggleYatsVisibilityMutation';

export function useUpdatePrimaryYat(eid: EmojiId) {
    const navigate = useNavigate();
    const [translate] = useTranslation('primary_yat');
    const { pathname } = useLocation();
    const { yatParam } = useParams();

    const dispatch = useAppDispatch();
    const invalidateQueries = useQueryInvalidation([USER_YATS_QUERY_CACHE, eid, USER_PROFILE_YATS_CACHE]);

    const { addToast } = useToastStore();
    const { mutateAsync: updatePrimaryYat } = useUpdatePrimaryYatMutation();
    //const { showTokens } = useToggleYatsVisibilityMutation();

    return useCallback(async () => {
        addToast({ isLoader: true, message: 'Workin’ on it…' });

        // TODO: We should check if the Yat was hidden, and only call
        // this function in that condition. Additionally, we can surface
        // another Toast to inform the user of the visibility change.
        // await showTokens({ eids: [eid] });

        updatePrimaryYat({ eid }).then((userResult) => {
            if (userResult.ok && userResult.value) {
                if (yatParam) {
                    const path = pathname.replace(`/${encodeURIComponent(yatParam)}`, '');
                    const newPath = `/${eid}${path}`;
                    navigate(newPath);
                }

                dispatch(getYatPageDetails(eid)).then((detailsResult) => {
                    if (detailsResult?.ok && detailsResult?.value?.version) {
                        dispatch(setPrimaryYat(eid));
                    }
                });

                invalidateQueries();

                addToast({
                    emoji: '🖖',
                    message: translate('yat_successfully_updated'),
                });
            }
        });
    }, [
        addToast,
        //showTokens,
        eid,
        updatePrimaryYat,
        yatParam,
        dispatch,
        invalidateQueries,
        translate,
        pathname,
        navigate,
    ]);
}
